import { c as _c } from "react/compiler-runtime";
import type { ElementType, ReactNode } from 'react';
import { styled, SxProps, Theme } from '@classify/react-core-components/styles';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import IconButton from '@classify/react-core-components/IconButton';
import Divider from '@classify/react-core-components/Divider';
import Box from '@classify/react-core-components/Box';
import Typography, { Props as TypographyProps } from '@classify/react-core-components/Typography';
import OverlayWrapper from './OverlayWrapper';
export interface BaseCourseItemTranslations {
  removeAriaLabel?: string;
}
export type Direction = 'row' | 'column';
const defaultDirection: Direction = 'row';
export interface Props {
  category: {
    name: string;
    color: string;
  };
  title: string;
  titleProps?: TypographyProps;
  illustration: ReactNode;
  children?: ReactNode;
  translations?: BaseCourseItemTranslations;
  isDraggable?: boolean;
  footer?: ReactNode;
  sx?: SxProps<Theme>;
  onRemove?: () => void;
  direction?: Direction;
  hasBottomBorder?: boolean;
  component?: ElementType;
  href?: string;
  to?: string;
  overlay?: ReactNode;
  'data-testid'?: string;
}
const Container = styled(Box, {
  shouldForwardProp: prop => !['direction', 'isInteractive'].includes(prop as string)
})<{
  direction: Direction;
  isInteractive: boolean;
}>(({
  theme: {
    palette,
    radius,
    shadows,
    transitions
  },
  direction,
  isInteractive
}) => ({
  backgroundColor: palette.layout.background.main,
  border: `1px solid ${palette.layout.surface.border}`,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderRadius: radius.surfaceCoreSmall,
  width: '100%',
  overflow: 'hidden',
  textDecoration: 'none',
  color: 'initial',
  ...(isInteractive && {
    '&:hover': {
      boxShadow: shadows.galleryCard,
      color: 'initial'
    },
    transition: transitions.create('box-shadow', {
      duration: transitions.duration.standard
    })
  }),
  ...(direction === 'column' && {
    ...(isInteractive && {
      '&:hover': {
        '& img': {
          transform: 'scale(1.05)'
        }
      },
      '& img': {
        transition: transitions.create('transform', {
          duration: transitions.duration.standard
        })
      }
    })
  })
}));
const ChildrenContainer = styled(Box)({
  marginTop: 'auto',
  p: {
    maxInlineSize: 768
  }
});
const IllustrationContainer = styled('div')({
  position: 'relative',
  flexShrink: 0
});
const Illustration = styled('img')<{
  direction: Direction;
}>(({
  theme: {
    radius,
    breakpoints,
    spacing
  },
  direction
}) => ({
  backgroundRepeat: 'no-repeat',
  flexShrink: 0,
  overflow: 'hidden',
  ...{
    row: {
      margin: spacing(2, 0, 2, 2),
      width: '72px',
      height: '72px',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      borderRadius: radius.field,
      [breakpoints.up('sm')]: {
        width: '108px',
        height: '108px'
      },
      [breakpoints.up('md')]: {
        width: '128px',
        height: '128px'
      },
      [breakpoints.up('lg')]: {
        width: '256px',
        height: '142px',
        backgroundPosition: 'center'
      }
    },
    column: {
      width: '100%',
      height: 'auto',
      display: 'block',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      paddingBottom: '56.25%',
      borderRadius: 'initial'
    }
  }[direction]
}));
const IconContainer = styled('div')({
  position: 'absolute',
  right: 0,
  top: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  marginLeft: 'auto'
});
const DraggableIcon = styled(DragIndicatorIcon)(({
  theme: {
    palette,
    spacing
  }
}) => ({
  alignSelf: 'center',
  marginRight: spacing(1),
  color: palette.icon.minor,
  cursor: 'grab'
}));
const Category = styled(Typography, {
  shouldForwardProp: prop => !['isRemovable'].includes(prop as string)
})<{
  isRemovable: boolean;
}>(({
  theme: {
    spacing
  },
  isRemovable
}) => ({
  ...(isRemovable && {
    marginRight: spacing(3)
  })
}));
const Content = styled('div', {
  shouldForwardProp: prop => !['isDraggable', 'direction'].includes(prop as string)
})<{
  isDraggable: boolean;
  direction: Direction;
}>(({
  theme: {
    spacing
  },
  isDraggable,
  direction
}) => ({
  display: 'flex',
  height: '100%',
  ...(isDraggable && {
    paddingLeft: spacing(1)
  }),
  ...(direction && {
    flexDirection: direction
  })
}));
const CourseMetaData = styled('div')(({
  theme: {
    spacing
  }
}) => ({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  padding: spacing(2)
}));
const Footer = styled('footer')(({
  theme: {
    spacing
  }
}) => ({
  display: 'flex',
  justifyContent: 'right',
  padding: spacing(1, 2)
}));
const BaseCourseItem = t0 => {
  const $ = _c(73);
  let category;
  let children;
  let footer;
  let illustration;
  let onRemove;
  let otherProps;
  let overlay;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let title;
  let titleProps;
  if ($[0] !== t0) {
    ({
      category,
      title,
      titleProps,
      illustration,
      translations: t1,
      children,
      isDraggable: t2,
      footer,
      onRemove,
      sx: t3,
      direction: t4,
      hasBottomBorder: t5,
      component: t6,
      overlay,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = category;
    $[2] = children;
    $[3] = footer;
    $[4] = illustration;
    $[5] = onRemove;
    $[6] = otherProps;
    $[7] = overlay;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = title;
    $[15] = titleProps;
  } else {
    category = $[1];
    children = $[2];
    footer = $[3];
    illustration = $[4];
    onRemove = $[5];
    otherProps = $[6];
    overlay = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    title = $[14];
    titleProps = $[15];
  }
  let t7;
  if ($[16] !== t1) {
    t7 = t1 === undefined ? {
      removeAriaLabel: "Remove"
    } : t1;
    $[16] = t1;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const translations = t7;
  const isDraggable = t2 === undefined ? false : t2;
  let t8;
  if ($[18] !== t3) {
    t8 = t3 === undefined ? {} : t3;
    $[18] = t3;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const sx = t8;
  const direction = t4 === undefined ? defaultDirection : t4;
  const hasBottomBorder = t5 === undefined ? false : t5;
  const component = t6 === undefined ? "article" : t6;
  const isInteractive = component === "a" || Boolean((otherProps as {
    href?: string;
  }).href) || Boolean((otherProps as {
    to?: string;
  }).to);
  let t9;
  if ($[20] !== category.color || $[21] !== direction || $[22] !== hasBottomBorder) {
    t9 = direction === "column" ? {
      borderBottom: hasBottomBorder ? `4px solid ${category.color}` : undefined
    } : undefined;
    $[20] = category.color;
    $[21] = direction;
    $[22] = hasBottomBorder;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== onRemove || $[25] !== translations) {
    t10 = onRemove && <IconContainer><IconButton aria-label={translations.removeAriaLabel} onClick={onRemove} tooltip={translations.removeAriaLabel as string}><CloseIcon /></IconButton></IconContainer>;
    $[24] = onRemove;
    $[25] = translations;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== isDraggable) {
    t11 = isDraggable && <DraggableIcon />;
    $[27] = isDraggable;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = `url(${illustration})`;
  let t13;
  if ($[29] !== t12) {
    t13 = {
      backgroundImage: t12
    };
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== direction || $[32] !== t13) {
    t14 = <Illustration sx={t13} data-testid="illustration" direction={direction} alt="" />;
    $[31] = direction;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== overlay) {
    t15 = overlay && <OverlayWrapper component="div" variant="subheadingSmall">{overlay}</OverlayWrapper>;
    $[34] = overlay;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t14 || $[37] !== t15) {
    t16 = <IllustrationContainer>{t14}{t15}</IllustrationContainer>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = Boolean(onRemove);
  let t18;
  if ($[39] !== category.color) {
    t18 = {
      color: category.color
    };
    $[39] = category.color;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== category.name || $[42] !== t17 || $[43] !== t18) {
    t19 = <Category variant="overline" isRemovable={t17} sx={t18}>{category.name}</Category>;
    $[41] = category.name;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      marginBottom: 1
    };
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== title || $[47] !== titleProps) {
    t21 = <Typography variant="h5" component="span" sx={t20} {...titleProps}>{title}</Typography>;
    $[46] = title;
    $[47] = titleProps;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== children) {
    t22 = <ChildrenContainer>{children}</ChildrenContainer>;
    $[49] = children;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t19 || $[52] !== t21 || $[53] !== t22) {
    t23 = <CourseMetaData>{t19}{t21}{t22}</CourseMetaData>;
    $[51] = t19;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== direction || $[56] !== isDraggable || $[57] !== t11 || $[58] !== t16 || $[59] !== t23) {
    t24 = <Content isDraggable={isDraggable} direction={direction}>{t11}{t16}{t23}</Content>;
    $[55] = direction;
    $[56] = isDraggable;
    $[57] = t11;
    $[58] = t16;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== footer) {
    t25 = footer && <><Divider aria-hidden={true} /><Footer>{footer}</Footer></>;
    $[61] = footer;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== component || $[64] !== direction || $[65] !== isInteractive || $[66] !== otherProps || $[67] !== sx || $[68] !== t10 || $[69] !== t24 || $[70] !== t25 || $[71] !== t9) {
    t26 = <Container style={t9} {...otherProps} sx={sx} direction={direction} isInteractive={isInteractive} as={component}>{t10}{t24}{t25}</Container>;
    $[63] = component;
    $[64] = direction;
    $[65] = isInteractive;
    $[66] = otherProps;
    $[67] = sx;
    $[68] = t10;
    $[69] = t24;
    $[70] = t25;
    $[71] = t9;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  return t26;
};
export default BaseCourseItem;